/**
 * A one-store IndexedDB cache.
 *
 * The server is the source of truth now, so this only holds the last snapshot seen for
 * each account. That is what makes the app readable on a plane: open it, see your budget,
 * but writes wait for a connection.
 *
 * Cached per account id, so signing in as the other user never shows the previous
 * account's data — not even for the instant before the network responds.
 */

const DB_NAME = 'personal-dashboard'
const DB_VERSION = 2
const STORE = 'cache'

let dbPromise: Promise<IDBDatabase> | null = null

function open(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)
    request.onupgradeneeded = () => {
      const db = request.result
      // Version 1 kept a store per entity; the server owns that shape now.
      for (const name of Array.from(db.objectStoreNames)) {
        if (name !== STORE) db.deleteObjectStore(name)
      }
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'key' })
      }
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
  return dbPromise
}

interface Entry<T> {
  key: string
  value: T
}

export async function cacheGet<T>(key: string): Promise<T | null> {
  try {
    const db = await open()
    return await new Promise<T | null>((resolve, reject) => {
      const request = db.transaction(STORE, 'readonly').objectStore(STORE).get(key)
      request.onsuccess = () => resolve((request.result as Entry<T> | undefined)?.value ?? null)
      request.onerror = () => reject(request.error)
    })
  } catch {
    return null
  }
}

export async function cacheSet<T>(key: string, value: T): Promise<void> {
  try {
    const db = await open()
    await new Promise<void>((resolve, reject) => {
      const request = db
        .transaction(STORE, 'readwrite')
        .objectStore(STORE)
        .put({ key, value } satisfies Entry<T>)
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  } catch {
    // A cache that cannot be written is not worth failing a render over.
  }
}

export async function cacheClear(): Promise<void> {
  try {
    const db = await open()
    await new Promise<void>((resolve, reject) => {
      const request = db.transaction(STORE, 'readwrite').objectStore(STORE).clear()
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  } catch {
    // ignore
  }
}

/**
 * Browsers can evict IndexedDB under storage pressure. The server has the real copy, but
 * losing the cache means no offline view at all, so ask for durable storage anyway.
 */
export async function requestPersistentStorage(): Promise<boolean> {
  if (!navigator.storage?.persist) return false
  if (await navigator.storage.persisted()) return true
  return navigator.storage.persist()
}

export function newId(): string {
  if (crypto.randomUUID) return crypto.randomUUID()
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}
